// Report tile — styled against the lens system (LensToolbar / TimelineStrip /
// LensSearchInput): semantic tokens only (mainDivider, mainButtonBg, mainText,
// mutedText, velBlue, inputFocus), glass tint via backgroundImage, glow as a
// state signal (hover/active/focus) rather than ambient animation. No local
// THEMES object, no JS style mutation — dark/light rides the tokens.
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Pause,
  Play,
  X,
  ExternalLink,
  GitCompareArrows,
  Image as ImageIcon,
  Link2,
  Copy,
  Check,
} from "lucide-react";
import { getReportProducts } from "./utils/reportUtils";
import { usePopup } from "../../contexts/PopupContext";

/* ── Helpers ── */
const formatTimestamp = (ts) => {
  if (ts == null) return "";
  try {
    const num = typeof ts === "string" ? Number(ts) : ts;
    const date = new Date(
      typeof num === "number" && num < 1e12 ? num * 1000 : num,
    );
    if (isNaN(date.getTime())) return String(ts);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return String(ts);
  }
};

const stripCitations = (text) =>
  (text || "").replace(/\[\d+\](?:\[\d+\])*/g, "");

// content ends with an inline "Sources: [1] url …" block that duplicates the
// structured `sources` array (and is often truncated mid-URL) — drop it.
const stripInlineSources = (text) =>
  (text || "").replace(/\n+Sources:\s*[\s\S]*$/i, "");

const applyInlineStyles = (text) => {
  if (!text) return null;
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
  let match,
    lastIndex = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const raw = match[0];
    if (raw.startsWith("**"))
      parts.push(
        <strong key={match.index} className="font-semibold text-mainText">
          {raw.slice(2, -2)}
        </strong>,
      );
    else
      parts.push(
        <em key={match.index} className="italic">
          {raw.slice(1, -1)}
        </em>,
      );
    lastIndex = match.index + raw.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
};

const renderInline = (text) => applyInlineStyles(stripCitations(text));

const renderMarkdown = (text) => {
  if (!text) return null;
  const lines = text.split("\n");
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      out.push(<div key={"gap-" + i} className="h-1.5" />);
      i++;
      continue;
    }
    if (/^-{3,}$/.test(line.trim())) {
      out.push(
        <hr key={"hr-" + i} className="border-t border-mainDivider my-2" />,
      );
      i++;
      continue;
    }
    if (line.startsWith("## ")) {
      out.push(
        <p
          key={"h2-" + i}
          className="mt-3 mb-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-mutedText"
        >
          {line.slice(3).replace(/\*\*/g, "")}
        </p>,
      );
      i++;
      continue;
    }
    if (line.startsWith("### ")) {
      out.push(
        <p
          key={"h3-" + i}
          className="mt-2 mb-0.5 text-[9px] font-semibold uppercase tracking-[0.18em] text-mutedText/75"
        >
          {line.slice(4).replace(/\*\*/g, "")}
        </p>,
      );
      i++;
      continue;
    }
    if (line.startsWith("# ")) {
      out.push(
        <p
          key={"h1-" + i}
          className="mt-3 mb-1 text-[13px] font-semibold text-mainText"
        >
          {line.slice(2).replace(/\*\*/g, "")}
        </p>,
      );
      i++;
      continue;
    }
    if (/^[-*] /.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*] /.test(lines[i])) {
        items.push(lines[i].slice(2));
        i++;
      }
      out.push(
        <ul key={"ul-" + i} className="my-1 flex flex-col gap-0.5 pl-2">
          {items.map((item, idx) => (
            <li
              key={idx}
              className="flex gap-2 text-[13px] leading-relaxed text-mainText/80"
            >
              <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-mutedText" />
              <span>{renderInline(item)}</span>
            </li>
          ))}
        </ul>,
      );
      continue;
    }
    out.push(
      <p
        key={"p-" + i}
        className="text-[13px] leading-relaxed text-mainText/80"
      >
        {renderInline(line)}
      </p>,
    );
    i++;
  }
  return out;
};

const computeWordDiff = (oldText, newText) => {
  const ow = (oldText || "").split(/(\s+)/);
  const nw = (newText || "").split(/(\s+)/);
  const m = ow.length,
    n = nw.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] =
        ow[i - 1] === nw[j - 1]
          ? dp[i - 1][j - 1] + 1
          : Math.max(dp[i - 1][j], dp[i][j - 1]);
  const result = [];
  let i = m,
    j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && ow[i - 1] === nw[j - 1]) {
      result.unshift({ type: "same", text: ow[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.unshift({ type: "added", text: nw[j - 1] });
      j--;
    } else {
      result.unshift({ type: "removed", text: ow[i - 1] });
      i--;
    }
  }
  return result;
};

/* ── Subcomponents ── */
const DiffView = ({ diffParts }) => {
  const oldParts = [],
    newParts = [];
  diffParts.forEach((p, i) => {
    if (p.type === "same") {
      oldParts.push(<span key={i}>{p.text}</span>);
      newParts.push(<span key={i}>{p.text}</span>);
    } else if (p.type === "removed")
      oldParts.push(
        <span
          key={i}
          className="bg-red-100 text-red-800 line-through dark:bg-red-900/30 dark:text-red-300"
        >
          {p.text}
        </span>,
      );
    else
      newParts.push(
        <span
          key={i}
          className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
        >
          {p.text}
        </span>,
      );
  });
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="border-r border-mainDivider pr-3 text-mainText/85">
        <span className="mb-2 block text-[9px] font-semibold uppercase tracking-[0.18em] text-mutedText">
          Previous
        </span>
        <p className="m-0 whitespace-pre-wrap text-[13px] leading-relaxed">
          {oldParts}
        </p>
      </div>
      <div className="pl-1 text-mainText/85">
        <span className="mb-2 block text-[9px] font-semibold uppercase tracking-[0.18em] text-mutedText">
          Current
        </span>
        <p className="m-0 whitespace-pre-wrap text-[13px] leading-relaxed">
          {newParts}
        </p>
      </div>
    </div>
  );
};

const SourcesDropdown = ({ sourcesList }) => {
  const [open, setOpen] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const copy = async (e, url, key) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(url);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1600);
    } catch {}
  };
  // Sources arrive as plain URL strings; dedupe (the API repeats URLs).
  const urls = [...new Set((sourcesList || []).filter(Boolean))];
  if (urls.length === 0) return null;
  return (
    <div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={[
          "flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] transition-colors duration-200",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60 rounded-sm",
          open ? "text-velBlue" : "text-mutedText hover:text-mainText",
        ].join(" ")}
      >
        <Link2 size={11} strokeWidth={2} />
        {urls.length} source{urls.length !== 1 ? "s" : ""}
        <ChevronDown
          size={11}
          className="transition-transform duration-150"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
          strokeWidth={2}
        />
      </button>
      {open && (
        <div
          className="mt-2 flex flex-col gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          {urls.map((url, i) => {
            const isCopied = copiedKey === i;
            const display = url
              .replace(/^https?:\/\/(www\.)?/, "")
              .replace(/\/$/, "");
            return (
              <div
                key={i}
                className="flex items-center gap-2 rounded-lg border border-mainDivider bg-mainButtonBg/30 px-3 py-1.5"
              >
                <span
                  className="min-w-0 flex-1 truncate font-mono text-[11px] text-mutedText"
                  title={url}
                >
                  {display}
                </span>
                <button
                  onClick={(e) => copy(e, url, i)}
                  className={[
                    "shrink-0 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60 rounded-sm",
                    isCopied
                      ? "text-velBlue"
                      : "text-mutedText hover:text-mainText",
                  ].join(" ")}
                  title="Copy URL"
                >
                  {isCopied ? (
                    <Check size={11} strokeWidth={2.5} />
                  ) : (
                    <Copy size={11} strokeWidth={2} />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Screenshots are tall mobile-web captures (0–4 per section), so the sources
// pattern fits better than an always-on thumbnail strip: a collapsed count
// trigger, expanding to a horizontally scrollable rail of portrait tiles.
// object-top: the top of a scraped page (header/product) is the identifying
// part, so that's the slice the fixed-height tile crops to.
const ScreenshotsDropdown = ({ screenshots }) => {
  const [open, setOpen] = useState(false);
  // Screenshots arrive as plain S3 image URL strings.
  const urls = (screenshots || []).filter(Boolean);
  if (urls.length === 0) return null;
  return (
    <div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={[
          "flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] transition-colors duration-200",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60 rounded-sm",
          open ? "text-velBlue" : "text-mutedText hover:text-mainText",
        ].join(" ")}
      >
        <ImageIcon size={11} strokeWidth={2} />
        {urls.length} screenshot{urls.length !== 1 ? "s" : ""}
        <ChevronDown
          size={11}
          className="transition-transform duration-150"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
          strokeWidth={2}
        />
      </button>
      {open && (
        <div
          className="mt-2 flex gap-2 overflow-x-auto pb-1"
          onClick={(e) => e.stopPropagation()}
        >
          {urls.map((src, i) => (
            <a
              key={i}
              href={src}
              target="_blank"
              rel="noopener noreferrer"
              title="Open full screenshot"
              className="block h-64 w-36 shrink-0 snap-start overflow-hidden rounded-lg border border-mainDivider bg-mainButtonBg/30 transition-all duration-200 hover:border-velBlue/40 hover:shadow-[0_0_14px_rgba(14,165,233,0.14)] focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
            >
              <img
                src={src}
                alt={`Screenshot ${i + 1}`}
                loading="lazy"
                className="h-full w-full object-cover object-top"
                onError={(e) => {
                  // Hide broken tiles (e.g. expired presigned S3 URLs)
                  e.currentTarget.parentElement.style.display = "none";
                }}
              />
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

const usePrefersReducedMotion = () => {
  const query = "(prefers-reduced-motion: reduce)";
  const [reduced, setReduced] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
};

const SLIDE_EASE = "cubic-bezier(.16, 1, .3, 1)";

const CONTROL_BUTTON =
  "inline-flex h-7 w-7 items-center justify-center rounded-lg border border-mainDivider bg-mainButtonBg/35 text-mutedText transition-all duration-200 hover:bg-mainButtonBg/60 hover:text-mainText focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60";

// Products are optional per section (the tile renders whatever
// getReportProducts zips out of product_images / product_title /
// product_risk). Shown as a carousel: image card left, title + risk right.
//
// Every slide is mounted and stacked in one grid cell, so the frame sizes to
// the tallest slide (no height jump between products) and slides can
// crossfade + drift in the direction of travel. Autoplay is driven by the
// progress fill's animationend, so pausing (hover, focus, offscreen, hidden
// tab, the pause button) is just animation-play-state. No autoplay under
// prefers-reduced-motion.
// Bigger view of one product, opened from the carousel through the app's
// popup host (Main.jsx owns the backdrop + click-outside close). Browses the
// same product list and reports the index back so the carousel lands on the
// last product viewed when the modal closes.
const ProductModal = ({ products, startIndex = 0, onClose, onNavigate }) => {
  const count = products.length;
  const multi = count > 1;
  const [index, setIndex] = useState(startIndex);
  const [broken, setBroken] = useState(() => new Set());
  const closeRef = useRef(null);
  const product = products[index] || products[0];
  const hasImage = product.image && !broken.has(index);
  const pad = (n) => String(n).padStart(2, "0");

  const go = useCallback(
    (step) => setIndex((i) => (i + step + count) % count),
    [count],
  );

  useEffect(() => {
    onNavigate?.(index);
  }, [index, onNavigate]);

  useEffect(() => {
    const restore = document.activeElement;
    closeRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (multi && e.key === "ArrowRight") go(1);
      else if (multi && e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      restore?.focus?.();
    };
  }, [go, multi, onClose]);

  const navButton =
    "inline-flex h-9 w-9 items-center justify-center rounded-full border border-mainDivider bg-mainBg/70 text-mutedText backdrop-blur-md transition-all duration-200 hover:bg-mainBg/90 hover:text-mainText focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-product-title"
      className="flex max-h-[88vh] w-[min(94vw,1040px)] flex-col overflow-y-auto rounded-xl border border-mainDivider bg-reortTileBg font-poppins font-normal text-mainText shadow-login-panel md:flex-row md:overflow-hidden"
    >
      {/* ── Image pane — product images are 400x800 mobile captures, so the
             pane sizes to a 1:2 frame (near native height on desktop) rather
             than letterboxing a tall image in a wide box. ── */}
      <div className="relative flex shrink-0 items-center justify-center overflow-hidden bg-mainButtonBg/30 p-5 md:p-6">
        {hasImage ? (
          <>
            <img
              key={`bg-${index}`}
              src={product.image}
              alt=""
              aria-hidden
              className="absolute inset-0 h-full w-full scale-125 animate-bg-fade-in object-cover opacity-30 blur-2xl"
            />
            <img
              key={index}
              src={product.image}
              alt={product.title || `Product ${index + 1}`}
              className="relative aspect-[1/2] h-[min(56vh,520px)] w-auto animate-bg-fade-in rounded-lg border border-mainDivider object-cover object-top shadow-[0_18px_44px_rgba(0,0,0,0.35)] md:h-[min(78vh,800px)]"
              onError={() => setBroken((s) => new Set(s).add(index))}
            />
          </>
        ) : (
          <div className="flex aspect-[1/2] h-[min(56vh,520px)] items-center justify-center rounded-lg border border-mainDivider md:h-[min(78vh,800px)]">
            <ImageIcon
              size={40}
              strokeWidth={1.5}
              className="text-mutedText/50"
            />
          </div>
        )}
        {multi && (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Previous product"
              className={`${navButton} absolute left-3 top-1/2 -translate-y-1/2`}
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Next product"
              className={`${navButton} absolute right-3 top-1/2 -translate-y-1/2`}
            >
              <ChevronRight size={16} />
            </button>
          </>
        )}
      </div>

      {/* ── Text pane ── */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="flex items-center justify-between gap-3 px-6 pb-2 pt-5">
          <span className="text-[10px] uppercase tabular-nums tracking-[0.18em] text-mutedText">
            {multi ? (
              <>
                <span className="text-velBlue">{pad(index + 1)}</span> /{" "}
                {pad(count)}
              </>
            ) : (
              "Product"
            )}
          </span>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-mutedText transition-colors duration-150 hover:bg-mainButtonBg/60 hover:text-mainText focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
          >
            <X size={15} />
          </button>
        </div>

        <div
          key={index}
          className="min-h-0 flex-1 animate-bg-fade-in px-6 pb-5 md:overflow-y-auto"
        >
          {product.title && (
            <h2
              id="report-product-title"
              className="mb-3 text-[19px] font-normal leading-snug text-mainText"
            >
              {stripCitations(product.title)}
            </h2>
          )}
          {product.risk && (
            <div className="[&_p]:text-[14px] [&_p]:leading-[1.7]">
              {renderMarkdown(product.risk)}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 border-t border-mainDivider px-6 py-4">
          {multi && (
            <div className="flex gap-2 overflow-x-auto pb-0.5">
              {products.map((p, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIndex(i)}
                  aria-label={`Show product ${i + 1}`}
                  aria-current={i === index}
                  className={[
                    "h-16 w-8 shrink-0 overflow-hidden rounded-md border bg-mainButtonBg/30 transition-all duration-200",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60",
                    i === index
                      ? "border-velBlue/60 shadow-[0_0_12px_rgba(14,165,233,0.25)]"
                      : "border-mainDivider opacity-60 hover:opacity-100",
                  ].join(" ")}
                >
                  {p.image && !broken.has(i) ? (
                    <img
                      src={p.image}
                      alt=""
                      className="h-full w-full object-cover object-top"
                      onError={() => setBroken((s) => new Set(s).add(i))}
                    />
                  ) : (
                    <ImageIcon
                      size={14}
                      className="mx-auto text-mutedText/50"
                    />
                  )}
                </button>
              ))}
            </div>
          )}
          {hasImage && (
            <a
              href={product.image}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 self-start rounded-sm text-[10px] uppercase tracking-[0.14em] text-mutedText transition-colors duration-200 hover:text-mainText focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
            >
              <ExternalLink size={11} strokeWidth={2} />
              Open original image
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const ProductCarousel = ({ products }) => {
  const count = products?.length || 0;
  const [{ index, prev, dir }, setNav] = useState({
    index: 0,
    prev: null,
    dir: 1,
  });
  const [userPaused, setUserPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [visible, setVisible] = useState(true);
  const [docHidden, setDocHidden] = useState(
    () => typeof document !== "undefined" && document.hidden,
  );
  const [broken, setBroken] = useState(() => new Set());
  const reducedMotion = usePrefersReducedMotion();
  const rootRef = useRef(null);
  const { openPopUp, closePopUp, isPopUpOpen } = usePopup();

  useEffect(() => {
    const onVis = () => setDocHidden(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const go = useCallback(
    (step) =>
      setNav((s) => ({
        index: (s.index + step + count) % count,
        prev: s.index,
        dir: step >= 0 ? 1 : -1,
      })),
    [count],
  );
  const goTo = useCallback(
    (i) =>
      setNav((s) =>
        i === s.index
          ? s
          : { index: i, prev: s.index, dir: i > s.index ? 1 : -1 },
      ),
    [],
  );

  const openProduct = (i) =>
    openPopUp(
      <ProductModal
        products={products}
        startIndex={i}
        onClose={closePopUp}
        onNavigate={goTo}
      />,
    );

  if (count === 0) return null;

  const multi = count > 1;
  const autoplay = multi && !reducedMotion;
  const paused =
    userPaused || hovered || focused || !visible || docHidden || isPopUpOpen;
  const pad = (n) => String(n).padStart(2, "0");

  // Entering slide comes from the travel direction, the leaving one exits
  // the opposite way; the rest wait (invisible) on the entering side.
  const offsetFor = (i) => (i === index ? 0 : i === prev ? -dir : dir);

  const slideStyle = (i, distance) => ({
    gridArea: "1 / 1",
    opacity: i === index ? 1 : 0,
    transform: `translate3d(${offsetFor(i) * distance}px, 0, 0)`,
    transition: reducedMotion
      ? "opacity 200ms linear"
      : `opacity 700ms ${SLIDE_EASE}, transform 900ms ${SLIDE_EASE}`,
  });

  return (
    <section
      ref={rootRef}
      aria-roledescription="carousel"
      aria-label="Products"
      className="mt-3 border-t border-mainDivider pt-3"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      // Keyboard focus only: after a mouse click (or the modal handing focus
      // back on close) autoplay should carry on.
      onFocus={(e) => {
        try {
          if (e.target.matches(":focus-visible")) setFocused(true);
        } catch {
          setFocused(true);
        }
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setFocused(false);
      }}
      onKeyDown={(e) => {
        if (!multi) return;
        if (e.key === "ArrowRight") {
          e.preventDefault();
          go(1);
        } else if (e.key === "ArrowLeft") {
          e.preventDefault();
          go(-1);
        }
      }}
    >
      <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.18em] text-mutedText">
        Products
      </p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[10rem_minmax(0,1fr)] sm:gap-5">
        {/* ── Image card — 1:2 to match the 400x800 mobile captures ── */}
        <div className="relative aspect-[1/2] w-40 overflow-hidden rounded-xl border border-mainDivider bg-mainButtonBg/30 shadow-[0_10px_30px_rgba(0,0,0,0.18)]">
          {products.map((product, i) => {
            const active = i === index;
            const hasImage = product.image && !broken.has(i);
            return (
              <div
                key={i}
                aria-hidden={!active}
                className="absolute inset-0"
                style={{
                  opacity: active ? 1 : 0,
                  transform: reducedMotion
                    ? "none"
                    : `translate3d(${offsetFor(i) * 18}%, 0, 0) scale(${active ? 1 : 0.94})`,
                  transition: reducedMotion
                    ? "opacity 200ms linear"
                    : `opacity 700ms ${SLIDE_EASE}, transform 900ms ${SLIDE_EASE}`,
                  pointerEvents: active ? "auto" : "none",
                }}
              >
                {hasImage ? (
                  <button
                    type="button"
                    onClick={() => openProduct(i)}
                    title="View product"
                    tabIndex={active ? 0 : -1}
                    className="block h-full w-full cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-inputFocus/60"
                  >
                    {/* object-top: if a capture isn't exactly 1:2, keep the
                        top of the page (the product header) in frame */}
                    <img
                      src={product.image}
                      alt={product.title || `Product ${i + 1}`}
                      loading="lazy"
                      className="h-full w-full origin-top object-cover object-top transition-transform ease-out"
                      style={{
                        // Slow Ken Burns push toward the top of the capture
                        // while a slide is on screen
                        transform:
                          active && !reducedMotion ? "scale(1.06)" : "scale(1)",
                        transitionDuration: active ? "7000ms" : "900ms",
                      }}
                      onError={() =>
                        // e.g. expired presigned S3 URLs
                        setBroken((s) => new Set(s).add(i))
                      }
                    />
                  </button>
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-mutedText/50">
                    <ImageIcon size={28} strokeWidth={1.5} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Text ── */}
        <div
          className="grid min-w-0"
          aria-live={autoplay && !paused ? "off" : "polite"}
        >
          {products.map((product, i) => {
            const active = i === index;
            return (
              <div
                key={i}
                role="group"
                aria-roledescription="slide"
                aria-label={`${i + 1} of ${count}`}
                aria-hidden={!active}
                inert={!active}
                className="min-w-0"
                style={{
                  ...slideStyle(i, 28),
                  pointerEvents: active ? "auto" : "none",
                }}
              >
                {multi && (
                  <p className="mb-1.5 text-[10px] tabular-nums tracking-[0.18em] text-velBlue">
                    {pad(i + 1)}
                    <span className="text-mutedText"> / {pad(count)}</span>
                  </p>
                )}
                {product.title && (
                  <button
                    type="button"
                    onClick={() => openProduct(i)}
                    className="mb-1.5 block rounded-sm text-left text-[14px] font-normal leading-snug text-mainText transition-colors duration-200 hover:text-velBlue focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
                  >
                    {stripCitations(product.title)}
                  </button>
                )}
                {product.risk && <div>{renderMarkdown(product.risk)}</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Controls ── */}
      {multi && (
        <div className="mt-3 flex items-center gap-3">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Previous product"
              className={CONTROL_BUTTON}
            >
              <ChevronLeft size={14} />
            </button>
            {autoplay && (
              <button
                type="button"
                onClick={() => setUserPaused((v) => !v)}
                aria-label={userPaused ? "Play carousel" : "Pause carousel"}
                className={CONTROL_BUTTON}
              >
                {userPaused ? <Play size={12} /> : <Pause size={12} />}
              </button>
            )}
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Next product"
              className={CONTROL_BUTTON}
            >
              <ChevronRight size={14} />
            </button>
          </div>

          {/* Segmented indicator: one pill per product, the active one fills
              as the autoplay timer. */}
          <div className="flex min-w-0 flex-1 items-center gap-1.5">
            {products.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                aria-label={`Go to product ${i + 1}`}
                aria-current={i === index}
                className="group flex h-4 min-w-0 flex-1 items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60 rounded-sm"
              >
                <span className="relative block h-[3px] w-full overflow-hidden rounded-full bg-mainDivider transition-colors duration-200 group-hover:bg-mutedText/50">
                  {i === index &&
                    (autoplay ? (
                      <span
                        className="absolute inset-0 origin-left animate-carousel-progress rounded-full bg-velBlue"
                        style={{
                          animationPlayState: paused ? "paused" : "running",
                        }}
                        onAnimationEnd={() => go(1)}
                      />
                    ) : (
                      <span className="absolute inset-0 rounded-full bg-velBlue" />
                    ))}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

const VersionDropdown = ({ versions, selectedIdx, onSelect }) => {
  const [open, setOpen] = useState(false);
  const selected = versions[selectedIdx];
  return (
    <div className="relative shrink-0">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="inline-flex items-center gap-1 rounded-lg border border-mainDivider bg-mainButtonBg/35 px-2 py-1 text-[10px] tabular-nums text-mutedText transition-all duration-200 hover:bg-mainButtonBg/60 hover:text-mainText focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
      >
        {formatTimestamp(selected?.timestamp)}
        <ChevronDown size={11} />
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          />
          <div className="absolute right-0 top-full z-20 mt-1 min-w-[160px] rounded-xl border border-mainDivider bg-mainBg/95 py-1 shadow-[0_18px_44px_rgba(0,0,0,0.45)] backdrop-blur-md">
            {versions.map((v, idx) => (
              <button
                key={`${v.document_id || ""}-${idx}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(idx);
                  setOpen(false);
                }}
                className={[
                  "w-full px-3 py-1.5 text-left text-[11px] tabular-nums transition-colors duration-150",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60",
                  idx === selectedIdx
                    ? "bg-velBlue/15 text-velBlue"
                    : "text-mainText/85 hover:bg-mainButtonBg/60",
                ].join(" ")}
              >
                {formatTimestamp(v.timestamp)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/* ── TileReport ── */
const TileReport = ({
  sectionKey = "",
  versions = [],
  selectedIdx = 0,
  onSelectVersion,
}) => {
  const [showChanges, setShowChanges] = useState(false);

  const selected = versions[selectedIdx] || versions[0];
  const previous = versions[selectedIdx + 1] || null;

  const diffParts = useMemo(() => {
    if (!showChanges || !previous || !selected) return null;
    return computeWordDiff(
      stripCitations(stripInlineSources(previous.content)),
      stripCitations(stripInlineSources(selected.content)),
    );
  }, [showChanges, selected, previous]);

  if (!selected) return null;

  const sectionTitle = (selected.section_title || sectionKey || "").trim();
  const products = getReportProducts(selected);
  const content = stripInlineSources(selected.content).trim();
  const timestamp = formatTimestamp(selected.timestamp);

  if (!sectionTitle && !content && products.length === 0) return null;

  return (
    <div
      className="relative overflow-hidden rounded-xl border border-mainDivider bg-reortTileBg font-poppins font-normal text-mainText transition-all duration-200 hover:border-velBlue/30 hover:shadow-[0_0_14px_rgba(14,165,233,0.10)] scroll-mt-20"
      style={{
        // backgroundImage, NOT the `background` shorthand — the shorthand
        // resets background-color and would kill bg-reortTileBg (same ruling
        // as LensToolbar / PanelFrame).
        backgroundImage:
          "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(14,165,233,0.05) 0%, transparent 100%)",
      }}
    >
      {/* ── Header — the TimelineStrip pattern: tracked label left, controls
             right, wrapping at narrow widths ── */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 pb-2.5 pt-3.5">
        <span className="min-w-0 truncate text-[11px] uppercase tracking-[0.24em] text-mainText/85">
          {sectionTitle}
        </span>

        <div className="flex items-center gap-2">
          {selected.window_label && (
            <span
              className="rounded-lg border border-velBlue/30 bg-velBlue/10 px-2.5 py-1 text-[10px] text-velBlue"
              title="Reporting window"
            >
              {selected.window_label}
            </span>
          )}
          {versions.length <= 1 && timestamp && (
            <span className="text-[10px] tabular-nums text-mutedText">
              {timestamp}
            </span>
          )}
          {!!previous && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowChanges((v) => !v);
              }}
              className={[
                "inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[10px] transition-all duration-200",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60",
                showChanges
                  ? "border border-velBlue/30 bg-velBlue/15 text-velBlue shadow-[0_0_14px_rgba(14,165,233,0.18)]"
                  : "border border-mainDivider bg-mainButtonBg/35 text-mutedText hover:bg-mainButtonBg/60 hover:text-mainText",
              ].join(" ")}
            >
              <GitCompareArrows size={11} />
              Changes
            </button>
          )}
          {versions.length > 1 && (
            <VersionDropdown
              versions={versions}
              selectedIdx={selectedIdx}
              onSelect={(idx) => onSelectVersion?.(idx)}
            />
          )}
        </div>
      </div>

      <div className="mx-4 border-t border-mainDivider" />

      {/* ── Body ── */}
      <div className="relative px-4 pb-1 pt-3">
        {showChanges && diffParts ? (
          <DiffView diffParts={diffParts} />
        ) : (
          <div>{renderMarkdown(content)}</div>
        )}
      </div>

      {/* ── Footer ── */}
      <div className="flex flex-col gap-2 px-4 pb-3.5 pt-1">
        {!showChanges && (
          // Keyed per version so switching versions restarts at product 1
          <ProductCarousel
            key={selected.document_id || selectedIdx}
            products={products}
          />
        )}
        {!showChanges && (
          <ScreenshotsDropdown screenshots={selected.screenshots} />
        )}
        {selected.sources?.length > 0 && (
          <SourcesDropdown sourcesList={selected.sources} />
        )}
      </div>
    </div>
  );
};

export default TileReport;
